import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useAuth } from "@/context/AuthContext";
import {
  DEFAULT_INTERFACE_LOCALE,
  localeNativeName,
  resolveInterfaceLocale,
  resolveLocalePriority,
  supportedInterfaceLocales,
  translateInterface,
} from "./core.mjs";

const LocalizationContext = createContext(null);
const ANONYMOUS_CACHE_KEY = "anokina.interfaceLocale.anonymous";
const userCacheKey = (userId) => `anokina.interfaceLocale.${userId}`;

function readCache(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

function writeCache(key, locale) {
  try { localStorage.setItem(key, locale); } catch { /* optional UX cache */ }
}

export function LocalizationProvider({ children }) {
  const { user, initializing, updateInterfaceLocale } = useAuth();
  const [locale, setLocaleState] = useState(DEFAULT_INTERFACE_LOCALE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initializing) return;
    const authenticated = Boolean(user && typeof user === "object");
    const cacheKey = authenticated ? userCacheKey(user.id) : ANONYMOUS_CACHE_KEY;
    const next = resolveLocalePriority({
      savedLocale: authenticated ? user.locale : null,
      cachedLocale: readCache(cacheKey),
      browserLocale: typeof navigator !== "undefined" ? navigator.language : null,
      authenticated,
    });
    setLocaleState(next);
    writeCache(cacheKey, next);
  }, [initializing, user]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(async (requested) => {
    const next = resolveInterfaceLocale(requested);
    const authenticated = Boolean(user && typeof user === "object");
    setSaving(true);
    try {
      if (authenticated) await updateInterfaceLocale(next);
      setLocaleState(next);
      writeCache(authenticated ? userCacheKey(user.id) : ANONYMOUS_CACHE_KEY, next);
      return next;
    } finally {
      setSaving(false);
    }
  }, [updateInterfaceLocale, user]);

  const value = useMemo(() => ({
    locale,
    saving,
    setLocale,
    supportedLocales: supportedInterfaceLocales(),
    localeNativeName,
    t: (key) => translateInterface(locale, key),
  }), [locale, saving, setLocale]);

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
}

export function useLocalization() {
  const context = useContext(LocalizationContext);
  if (!context) throw new Error("useLocalization must be used within LocalizationProvider");
  return context;
}
